import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { ReactComponent as FolderClosedSvg } from '@sketch/icons/folder-closed-17'
import { ReactComponent as SquareStack } from '@sketch/icons/stacks-3-17'
import { ReactComponent as DocumentSvg } from '@sketch/icons/document-17'
import { ReactComponent as CanvasPageSvg } from '@sketch/icons/canvas-17'
import { ReactComponent as FrameSvg } from '@sketch/icons/frame-17'
import { ReactComponent as ChevronsSVG } from '@sketch/icons/chevrons-up-down-17'
import { ReactComponent as FlagSvg } from '@sketch/icons/flag-17'
import { ReactComponent as PrototypeSvg } from '@sketch/icons/prototype-17'
import { ReactComponent as ComponentsSvg } from '@sketch/icons/misc-components-view-17'
import { ReactComponent as TemplateSvg } from '@sketch/icons/templates-17'
import { ReactComponent as SymbolSvg } from '@sketch/icons/symbol-17'

import { Tooltip, Dropdown, Separator, Pill } from '@sketch/components'

interface LinkProps {
  $secondary?: boolean
  $isSymbol?: boolean
}

const linkStyles = css<LinkProps>`
  font-size: ${({ theme }) => theme.fontSizes.D};
  display: inline-flex;
  align-items: center;
  padding: 8px 0;
  overflow: hidden;
  position: relative;
  color: inherit;
  font-weight: inherit;
  line-height: 1.2;
  user-select: none;
  min-width: 20px;
  flex-shrink: 10e6;
  max-width: calc(100% - 16px);
  justify-content: center;
  outline-offset: -1px; /* To avoid cutting focus ring */

  &:hover,
  &:focus,
  &:active,
  &:last-child {
    color: ${({ theme, $isSymbol }) =>
      $isSymbol
        ? theme.colors.inspect.symbol.A
        : theme.colors.foreground.secondary.B};
  }

  &:last-child {
    /*
    This flex-shrink value is intentionally much smaller than the default value (10e6).
    In practice this means the last element will only shrink after the other siblings.
    */
    flex-shrink: 1;
  }

  ${({ theme, $isSymbol }) =>
    $isSymbol &&
    css`
      color: ${theme.colors.inspect.symbol.A};
    `}

  &&& {
    ${({ theme, $secondary }) =>
      $secondary &&
      css`
        font-weight: 400;
        color: ${theme.colors.foreground.secondary.C};
      `}
  }
`

export const NavigationLink = styled(NavLink)<LinkProps>`
  ${linkStyles}
`

export const UnlinkedNavigationItem = styled.div<LinkProps>`
  ${linkStyles}
  font-weight: 400;
`

const iconStyles = css`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  width: 17px;
  height: 17px;
  flex: none;
`

export const ProjectIcon = styled(FolderClosedSvg)`
  ${iconStyles}
`

export const CollectionIcon = styled(SquareStack)`
  ${iconStyles}
`

export const DocumentIcon = styled(DocumentSvg)`
  ${iconStyles}
`

export const TemplateIcon = styled(TemplateSvg)`
  ${iconStyles}
`

export const PageIcon = styled(CanvasPageSvg)`
  ${iconStyles}
`

export const FrameIcon = styled(FrameSvg)`
  ${iconStyles}
`

export const PrototypeIcon = styled(PrototypeSvg)`
  ${iconStyles}
`

export const PrototypeStartPointIcon = styled(FlagSvg)`
  ${iconStyles}
`

export const ComponentsIcon = styled(ComponentsSvg)`
  ${iconStyles}
`

export const SymbolIcon = styled(SymbolSvg)`
  ${iconStyles}
  color: ${({ theme }) => theme.colors.inspect.symbol.A};
`

type ItemLabelProps = {
  noIcon?: boolean
}

export const ItemLabel = styled.span<ItemLabelProps>`
  margin-left: ${props => (props.noIcon ? 0 : 6)}px;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ItemTooltip = styled(Tooltip)`
  user-select: none;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

export const ChevronsIcon = styled(ChevronsSVG).attrs({
  role: 'img',
  'aria-label': 'Zoom',
})<{ $isSymbol?: boolean }>`
  width: 17px;
  height: 17px;
  margin: 0 0 0 4px;
  flex: none;
  color: ${({ theme, $isSymbol }) =>
    $isSymbol
      ? theme.colors.inspect.symbol.A
      : theme.colors.foreground.secondary.A};
`

export const ViewWrapper = styled.div`
  position: sticky;
  bottom: 56px;
  background: ${({ theme }) => theme.colors.background.secondary.A};
`

export const DropdownItem = styled(Dropdown.Item.NavLink)`
  align-items: center;
  display: flex;

  svg {
    color: ${({ theme }) => theme.colors.foreground.secondary.D};
    width: 17px;
    height: 17px;
    margin-right: 12px;
    /* Adjust the icons */
    margin-bottom: 1px; /* stylelint-disable scales/space */
  }

  /* We need to set back the active/hover states
  because we override them for svg */
  && {
    &.focus,
    &.active,
    &[data-current] {
      svg {
        color: ${({ theme }) => theme.colors.sketch.A};
      }
    }
  }
`

export const StyledSeparator = styled(Separator)`
  margin: 16px 0;
`

export const Content = styled.div``

export const ArchivePill = styled(Pill).attrs({
  variant: 'secondary',
  children: 'Archived',
})`
  margin-left: 8px;
`
